/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Field, Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Form as NXUIForm, Table, Theme } from '@nexigendigital/nxui'
import { domainAPI, useAddDNSRecordsMutation } from 'api/domain'
import { DNSRecordType } from 'models/enums'
import { FormRecord, RecordType } from 'models/domain'
import { regex } from 'helpers/utils'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function AddRecordForm() {
    const addRecordFormRef = useRef<FormikProps<Omit<FormRecord, 'id'>>>(null)
    const id = +location.pathname?.split('/')[2]
    const { data: { records } = { records: [] } } = domainAPI.endpoints.dnsRecords.useQueryState(id)
    const { data: domain } = domainAPI.endpoints.domain.useQueryState(id)
    const [addDnsRecords, { isSuccess }] = useAddDNSRecordsMutation({ fixedCacheKey: 'add-dns-record' })
    const columns = (type: RecordType) => {
        const sharedColumns = [
            {
                Header: 'Type',
                accessor: 'type'
            },
            {
                Header: 'Hostname',
                accessor: 'hostname'
            }
        ]

        if (type === 'MX') {
            return [
                ...sharedColumns,
                {
                    Header: 'Content',
                    accessor: 'content'
                },
                {
                    Header: 'TTL',
                    accessor: 'ttl'
                },
                {
                    Header: 'Priority',
                    accessor: 'priority'
                },
                {
                    Header: 'Action',
                    accessor: 'action',
                    align: 'right'
                }
            ]
        }

        if (type === 'SRV') {
            return [
                ...sharedColumns,
                {
                    Header: 'Weight',
                    accessor: 'weight'
                },
                {
                    Header: 'Port',
                    accessor: 'port'
                },
                {
                    Header: 'Destination',
                    accessor: 'destination'
                },
                {
                    Header: 'TTL',
                    accessor: 'ttl'
                },
                {
                    Header: 'Priority',
                    accessor: 'priority'
                },
                {
                    Header: 'Action',
                    accessor: 'action',
                    align: 'right'
                }
            ]
        }

        return [
            ...sharedColumns,
            {
                Header: 'Content',
                accessor: 'content'
            },
            {
                Header: 'TTL',
                accessor: 'ttl'
            },
            {
                Header: 'Action',
                accessor: 'action',
                align: 'right'
            }
        ]
    }
    const rows = (type: RecordType) => {
        return [{
            type: (
                <Field
                    label={'Type'}
                    name={'type'}
                    type={'select'}
                    list={Object.keys(DNSRecordType).filter((key) => key !== 'SOA').map((key: string) => ({
                        label: key,
                        value: key
                    }))}
                    component={NXUIForm.SelectField}
                />
            ),
            hostname: (
                <Field
                    label={'Hostname'}
                    placeholder={!['CNAME', 'NS', 'SRV'].includes(type) ? 'Use @ for root domain' : ''}
                    name={'hostname'}
                    type={'text'}
                    component={NXUIForm.InputField}
                />
            ),
            content: (
                type === 'SRV'
                    ? null
                    : (
                        <Field
                            label={handleContentLabel(type)}
                            name={'content'}
                            type={'text'}
                            component={NXUIForm.InputField}
                        />
                    )
            ),
            weight: (
                type === 'SRV'
                    ? (
                        <Field
                            label={'Weight'}
                            name={'weight'}
                            type={'text'}
                            component={NXUIForm.InputField}
                        />
                    )
                    : null
            ),
            port: (
                type === 'SRV'
                    ? (
                        <Field
                            label={'Port'}
                            name={'port'}
                            type={'text'}
                            component={NXUIForm.InputField}
                        />
                    )
                    : null
            ),
            destination: (
                type === 'SRV'
                    ? (
                        <Field
                            label={'Destination'}
                            name={'destination'}
                            type={'text'}
                            component={NXUIForm.InputField}
                        />
                    )
                    : null
            ),
            priority: (
                type === 'MX' || type === 'SRV'
                    ? (
                        <SmallField>
                            <Field
                                label={'Priority'}
                                name={'priority'}
                                type={'text'}
                                component={NXUIForm.InputField}
                            />
                        </SmallField>
                    )
                    : null
            ),
            ttl: (
                <SmallField>
                    <Field
                        label={'TTL'}
                        name={'ttl'}
                        type={'text'}
                        component={NXUIForm.InputField}
                    />
                </SmallField>
            ),
            action: (
                <Anchor color={'primary'} type={'submit'} form={'addRecordForm'}>
                    Add
                </Anchor>
            )
        }]
    }

    function handleContentLabel(type: RecordType = 'A') {
        switch (type) {
            case 'A':
                return 'IPv4 Address'
            case 'AAAA':
                return 'IPv6 Address'
            case 'CNAME':
                return 'Target'
            case 'MX':
                return 'Mail Server'
            case 'NS':
                return 'Nameserver'
            case 'SRV':
                return 'Destination'
            default:
                return 'Content'
        }
    }

    function handleDNSRecordDescription(type: RecordType) {
        switch (type) {
            case 'A':
            case 'AAAA':
                return ' points to '
            case 'CNAME':
                return ' is an alias of '
            case 'MX':
                return ' handles mail for '
            case 'NS':
                return ' is managed by '
            case 'TXT':
                return ' has a record with content '
            case 'SRV':
                return ' points to '
            default:
                return ''
        }
    }

    function handleHostname(hostname: string, type: RecordType) {
        if (hostname === '') {
            return '[Hostname]'
        }

        if (hostname === '@' && !['CNAME', 'SRV'].includes(type)) {
            return domain?.domain
        }

        return `${hostname}.${domain?.domain}`
    }

    function handleContent(content: string = '', type: RecordType) {
        if (content === '') {
            return `[${handleContentLabel(type)}]`
        }

        return content
    }

    function previewDNSRecordResult({ type, hostname, content, port, destination }: FormRecord) {
        switch (type) {
            case 'A':
            case 'AAAA':
            case 'CNAME':
            case 'NS':
            case 'TXT':
                return (
                    <Result>
                        <Value>{handleHostname(hostname, type)}</Value>
                        {handleDNSRecordDescription(type)}
                        <Value>{handleContent(content, type)}</Value>
                    </Result>
                )
            case 'MX':
                return (
                    <Result>
                        <Value>{handleContent(content, type)}</Value>
                        {handleDNSRecordDescription(type)}
                        <Value>{handleHostname(hostname, type)}</Value>
                    </Result>
                )
            case 'SRV':
                return (
                    <Result>
                        <Value>{handleHostname(hostname, type)}</Value>
                        {handleDNSRecordDescription(type)}
                        <Value>{handleContent(destination, type)}</Value>
                        {' and listens on '}
                        <Value>{port === '' ? '[Port]' : port}</Value>
                    </Result>
                )
            default:
                return hostname
        }
    }

    /**********************************************************************************************************
     *   SIDE EFFECTS
     **********************************************************************************************************/
    useEffect(() => {
        if (isSuccess && addRecordFormRef.current) {
            addRecordFormRef.current.resetForm()
        }
    }, [isSuccess])

    return (
        <Formik
            innerRef={addRecordFormRef}
            enableReinitialize={true}
            initialValues={{
                type: 'A',
                hostname: '',
                content: '',
                priority: '5',
                ttl: '14400',
                weight: '',
                port: '',
                destination: ''
            }}
            validationSchema={
                Yup.object().shape({
                    hostname: Yup.string()
                        .when('type', {
                            is: (type: DNSRecordType) => Object.keys(DNSRecordType).filter((type) => ['SRV', 'CNAME', 'NS'].includes(type)).includes(type),
                            then: Yup.string().required('Required')
                        }),
                    content: Yup.string()
                        .when('type', {
                            is: 'CNAME',
                            then: Yup.string().matches(regex.domainWithExtension, 'Target must be a domain')
                        })
                        .when('type', {
                            is: 'NS',
                            then: Yup.string().matches(regex.domainWithExtension, 'Nameserver must be a domain')
                        })
                        .when('type', {
                            is: 'MX',
                            then: Yup.string().matches(regex.domainWithExtension, 'Mail Server must be a domain')
                        })
                        .when('type', {
                            is: (type: DNSRecordType) => Object.keys(DNSRecordType).filter((type) => type !== 'SRV').includes(type),
                            then: Yup.string().required('Required')
                        }),
                    weight: Yup.string()
                        .when('type', {
                            is: 'SRV',
                            then: Yup.string().required('Required')
                        }),
                    port: Yup.string()
                        .when('type', {
                            is: 'SRV',
                            then: Yup.string().required('Required')
                        }),
                    destination: Yup.string()
                        .when('type', {
                            is: 'SRV',
                            then: Yup.string().required('Required')
                        }),
                    ttl: Yup.string().required('Required')
                })
            }
            onSubmit={({ hostname, content, type, destination, weight, port, ttl, priority }, { setSubmitting }) => {
                const newRecord = () => {
                    if (type === 'SRV') {
                        return {
                            type,
                            ttl,
                            priority,
                            content: `${weight} ${port} ${destination}`,
                            hostname: hostname === '@' ? domain?.domain : `${hostname}.${domain?.domain}`
                        }
                    }

                    if (type === 'MX') {
                        return {
                            type,
                            content,
                            ttl,
                            priority,
                            hostname: hostname === '@' || hostname === '' ? domain?.domain : `${hostname}.${domain?.domain}`
                        }
                    }

                    return {
                        type,
                        content,
                        ttl,
                        hostname: (hostname === '@' || hostname === '') && type !== 'CNAME' ? domain?.domain : `${hostname}.${domain?.domain}`
                    }
                }

                addDnsRecords({ id, records: [...records, newRecord()] })
                setSubmitting(false)
            }}
        >
            {({ values: { type, hostname, content, port, destination } }) => (
                <FormWrapper id={'addRecordForm'}>
                    {previewDNSRecordResult({ type, hostname, content, port, destination })}
                    <Table
                        conditions={{
                            sort: false,
                            header: false
                        }}
                        render={{
                            columns: () => columns(type),
                            data: () => rows(type)
                        }}
                    />
                </FormWrapper>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const FormWrapper = styled(Form)`
    border-top: 1px solid ${props => (props.theme['gray--100'])};
    padding-top: 22px;

    tr {
        border: none;
    }

    td:first-child {
        min-width: 140px;
        
        > div {
            margin-bottom: 24px;
        }
    }

    td:last-child {
        height: 86px;
    }
`

const Result = styled.p`
    padding: 0 20px 22px 20px;
    color: ${props => (props.theme['text--100'])};
    ${Theme.Functions.setFont('Inter', 500)};
`

const Value = styled.span`
    color: ${props => (props.theme['black--100'])};
`

const SmallField = styled.div`
    width: 80px;
`
